<template>
    <div
        class="dk-color-picker tw-flex tw-flex-col tw-items-center"
        :class="{ 'lg:tw-items-start': isThreeStepsDkPromo, '!tw-items-start lg:!tw-items-center': isVdpEcommerce }"
    >
        <template v-if="showColorName">
            <p
                v-if="isVdpEcommerce"
                class="dk-color-picker__color-name tw-mb-2 tw-text-xs tw-text-base-subtle lg:tw-text-lg"
            >
                <span class="tw-mr-1">Colore selezionato:</span>
                <span class="tw-font-semibold">{{ currentColorName }}</span>
            </p>
            <p v-else class="dk-color-picker__color-name body-md tw-mb-6">
                {{ currentColorName }}
            </p>
        </template>
        <div
            class="dk-color-picker__thumbs tw-flex tw-justify-center tw-flex-wrap tw-w-full"
            :class="thumbsClass"
            :style="parentWidth"
        >
            <dk-color-thumb
                v-for="(color, index) in colorsToShow"
                :key="`thumb-${index}`"
                :class="thumbWidthClass"
                :color="color"
                :size="size"
                :is-selected="index === currentSelection"
                @select-color="updateCurrentSelection(index)"
            />
            <dk-color-thumb
                v-if="showMore"
                class="dk-color-picker__show-more"
                :class="thumbWidthClass"
                :size="size"
                :show-more="showMore"
                :is-three-steps-dk-promo="isThreeStepsDkPromo || hasDealerSelectionLayout"
                @show-more="showAllColors"
            />
            <dk-color-thumb
                v-if="(isThreeStepsDkPromo || hasDealerSelectionLayout) && showLess"
                class="dk-color-picker__show-less"
                :class="thumbWidthClass"
                :size="size"
                :show-less="showLess"
                :is-three-steps-dk-promo="isThreeStepsDkPromo || hasDealerSelectionLayout"
                @show-less="showLessColors"
            />
        </div>
        <p v-if="disclaimer" class="dk-color-picker__disclaimer tw-mt-4 micro tw-text-base-subtle">
            {{ disclaimer }}
        </p>
    </div>
</template>

<script>
import DkColorThumb from '@v4/common/components/atoms/DkColorThumb.vue';
import { EventBus } from '@v4/common/utils/event-bus';
import { colorImageUrlStore } from '@v4/lead-form/store/index';
import { COLOR_IMAGE_URL_MUTATIONS } from '@v4/lead-form/store/mutations';

export default {
    name: 'DkColorPicker',
    components: {
        DkColorThumb,
    },
    props: {
        showColorName: {
            type: Boolean,
            default: true,
        },
        /**
         * Array of objects of type:
         * active:true
         * baseColourHex:"#000000"
         * basePrice:614.75
         * colorImage:Object
         * customDescription:null
         * description:"Solid Black (pastello)"
         * equipmentId:"422950"
         * finalPrice:"€ 750"
         * group:"Vernice pastello Solid Black"
         * manufacturerCode:"601"
         * msrpPrice:750
         * order:1
         * primaryHex:"#50483d"
         * secondaryHex:null
         */
        colors: {
            type: Array,
            required: true,
        },
        /**
         * Max number of initial colors to display, including 'show more'.
         */
        maxLanding: {
            type: Number,
            default: 5,
        },
        /**
         * Max number of colors per line after clicking on 'show more'.
         * Normally, it's 10 for desktop and 5 for mobile.
         */
        maxPerLine: {
            type: Number,
            default: 10,
        },
        /**
         * Available sizes: 's', 'l'.
         */
        size: {
            type: String,
            default: '',
        },
        /**
         * The disclaimer to show under the color picker.
         */
        disclaimer: {
            type: String,
            default: '',
        },
        /**
         * A flag to set to true when we want to show all available colors.
         */
        showAll: {
            type: Boolean,
            default: false,
        },
        /*
         * A flag for the custom classes for the 3-steps DkPromo page.
         */
        isThreeStepsDkPromo: {
            type: Boolean,
            default: false,
        },
        /*
         * A flag for the custom classes for the pages
         * that has dealer selection with a map.
         */
        hasDealerSelectionLayout: {
            type: Boolean,
            default: false,
        },
        isInsideModal: {
            type: Boolean,
            default: false,
        },
        isVdpEcommerce: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentSelection: 0,
            colorsClone: [],
            colorsToShow: [],
            colorsToHide: [],
            showLess: false,
        };
    },
    computed: {
        showMore() {
            return this.showAll ? !this.showAll : this.colorsToHide.length > 0;
        },
        currentColorName() {
            if (this.colorsClone.length > 0) {
                return this.colorsClone[this.currentSelection].description;
            }
            return '';
        },
        currentColorImageUrl() {
            return (
                this.colors[this.currentSelection].colorImage.image800 ||
                this.colors[this.currentSelection].colorImage.imageOriginal
            );
        },
        parentGapSize() {
            return this.size === 's' ? 'tw-gap-2' : 'tw-gap-4';
        },
        thumbWidthClass() {
            const thumbWidth = 100 / this.maxPerLine;
            return `tw-w-[${thumbWidth}%]`;
        },
        parentWidth() {
            const thumbWidthOnDesktop = 72;
            const thumbWidthOnMobile = 56;
            const gapOnDesktop = 16;
            const gapOnMobile = 8;

            const thumbWidth =
                this.size === 's' ? thumbWidthOnMobile * this.maxPerLine : thumbWidthOnDesktop * this.maxPerLine;
            const gapWith =
                this.size === 's' ? gapOnMobile * (this.maxPerLine - 1) : gapOnDesktop * (this.maxPerLine - 1);
            return { maxWidth: `${thumbWidth + gapWith}px` };
        },
        thumbsClass() {
            return {
                [this.parentGapSize]: true,
                'max-lg:!tw-max-w-card-mobile': this.hasDealerSelectionLayout,
                'lg:tw-justify-start': this.isThreeStepsDkPromo && !this.isVdpEcommerce,
                'tw-gap-2': this.isThreeStepsDkPromo || this.hasDealerSelectionLayout,
                '!tw-gap-2': this.isVdpEcommerce,
                'lg:tw-gap-3':
                    !this.isInsideModal &&
                    ((this.isThreeStepsDkPromo && this.maxLanding <= 6) || this.hasDealerSelectionLayout),
                'lg:tw-gap-4':
                    !this.isInsideModal &&
                    ((this.isThreeStepsDkPromo && this.maxLanding > 6) || this.hasDealerSelectionLayout),
            };
        },
    },
    watch: {
        maxLanding(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.initializeColors();
            }
        },
    },
    mounted() {
        this.initializeColors();
        if (this.isInsideModal) {
            this.checkActiveColor();
        }
    },
    methods: {
        initializeColors() {
            this.colorsClone = JSON.parse(JSON.stringify(this.colors));
            const hasColorsToHide = this.showAll ? false : this.colorsClone.length > this.maxLanding;
            this.colorsToShow = hasColorsToHide ? this.colorsClone.slice(0, this.maxLanding - 1) : this.colorsClone;
            this.colorsToHide = hasColorsToHide ? this.colorsClone.slice(this.colorsToShow.length) : [];
            colorImageUrlStore.commit(COLOR_IMAGE_URL_MUTATIONS.SET_COLOR_IMAGE_URL, this.currentColorImageUrl);
        },
        updateCurrentSelection(index) {
            if (this.currentSelection !== index) {
                // Reset all colors to inactive
                this.colorsClone.forEach((color) => (color.active = false));

                // Set the selected color to active
                this.colorsClone[index].active = true;
                this.currentSelection = index;

                const colorIndexFromOriginalColors = this.getIndexFromOriginalColors(index);
                this.$emit('item-click', colorIndexFromOriginalColors);
                colorImageUrlStore.commit(COLOR_IMAGE_URL_MUTATIONS.SET_COLOR_IMAGE_URL, this.currentColorImageUrl);
            }
        },
        showAllColors() {
            this.colorsToShow = [...this.colorsToShow, ...this.colorsToHide];
            this.colorsToHide = [];

            if (this.isThreeStepsDkPromo || this.hasDealerSelectionLayout) {
                this.toggleShowLess();
                EventBus.$emit('DkColorPicker:show-less-toggle', this.showLess);
            }
        },
        showLessColors(dryRun = false) {
            this.colorsToShow = this.colorsClone.slice(0, this.maxLanding - 1);
            this.colorsToHide = this.colorsClone.slice(this.colorsToShow.length);

            if (!dryRun) {
                this.toggleShowLess();
            }

            if (this.currentSelection > this.maxLanding - 2) {
                // Handle the case where the user has selected a color
                // from the colorsToHide (color was originally hidden).
                // The selected color will be moved to the top of the colorsToShow,
                // it will be the first color in the selection once the color selection is minimized.
                this.reorganizeColorsOrder(this.colorsToHide.findIndex((color) => color.active === true));
            }

            if (!dryRun) {
                EventBus.$emit('DkColorPicker:show-less-toggle', this.showLess);
            }
        },
        reorganizeColorsOrder(index) {
            if (index === -1) {
                return;
            }

            // Move the color from colorsToHide to colorsToShow
            this.colorsToShow.unshift(this.colorsToHide.splice(index, 1)[0]);

            // Move the last color of colorsToShow to the start of colorsToHide
            this.colorsToHide.unshift(this.colorsToShow.pop());

            // Rebuild the colors clone array with the updated order
            this.colorsClone = [...this.colorsToShow, ...this.colorsToHide];

            this.currentSelection = 0;

            const colorIndexFromOriginalColors = this.getIndexFromOriginalColors(0);
            this.$emit('order-change', colorIndexFromOriginalColors);
        },
        getIndexFromOriginalColors(index) {
            // Find the index of the color in the original colors array and emit
            // the order-change event with the index from the original colors array
            // because the array from the props has a different order than the array from the cloned colors.
            return this.colors.findIndex((color) => color.description === this.colorsClone[index].description);
        },
        checkActiveColor() {
            const index = this.colorsClone.findIndex((color) => color.active === true);

            if (index !== -1 && index !== this.currentSelection) {
                this.updateCurrentSelection(index);
            }

            if (index > this.colorsToShow.length - 1) {
                this.showLessColors(true);
            }
        },
        toggleShowLess() {
            this.showLess = !this.showLess;
            this.$emit('show-less', this.showLess);
        },
    },
};
</script>
